import type {EditorView, KeyBinding} from '@codemirror/view'
import {CheckIcon, HeadingIcon} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import {KeybindingHint} from '@primer/react/experimental'
import {use, useCallback, useMemo} from 'react'

import {
  adjustHeadingLevel,
  canAdjustHeadingLevel,
  type Direction,
  getHeadingLevelIfUniform,
  type HeadingLevel,
} from '../codemirror/commands/headings/format-heading'
import {
  formatHeading1Keybinding,
  formatHeading2Keybinding,
  formatHeading3Keybinding,
  formatHeading4Keybinding,
  formatHeading5Keybinding,
  formatHeading6Keybinding,
} from '../codemirror/extensions/markdown-keymap'
import {useHybridEditorTelemetry} from '../telemetry'
import styles from './Toolbar.module.css'
import {ToolbarContext} from './ToolbarContext'
import {formatKeybindingHint} from './util'

const CheckIconPlaceholder = () => <div className={styles.menuLeadingVisualPlaceholder} />

const generateTrailingVisual = ({keys}: {keys: string}) =>
  function TrailingVisual() {
    return <KeybindingHint className={styles.menuKeybindingHint} keys={keys} />
  }

const getTrailingVisual = (keybinding: KeyBinding) => {
  const keys = formatKeybindingHint(keybinding.key)
  if (!keys) return undefined
  return generateTrailingVisual({keys})
}

export const HeadingMenu = () => {
  const telemetry = useHybridEditorTelemetry()
  const {view, selectedNodes} = use(ToolbarContext)

  const withinHeadingSelection: HeadingLevel | undefined = useMemo(() => {
    // `selectedNodes` (the selection snapshot — a fresh array on every selection change) is the reactive
    // trigger: the level itself is read from the editor's mutable, React-Compiler-invisible `view` state.
    // Referencing it in control flow keeps it a tracked dependency that can't be dead-code-eliminated.
    // `selectedNodes` is always a (truthy) array, so this guard never changes the result.
    if (!view || !selectedNodes) return undefined
    return getHeadingLevelIfUniform(view)
  }, [selectedNodes, view])

  const isHeadingLevelChangeEnabled = useCallback(
    (direction: Direction) => {
      // See note above: touching `selectedNodes` anchors this to selection changes without altering the
      // result (it is always a truthy array).
      if (!view || !selectedNodes) return false
      return canAdjustHeadingLevel(view, direction)
    },
    [selectedNodes, view],
  )

  const getLeadingVisual = useCallback(
    (level: number) => {
      // Determine if we should show any visual indicators. If no heading is selected, don't show any visual
      const hasHeadingSelection = withinHeadingSelection !== undefined
      if (!hasHeadingSelection) {
        return undefined
      }

      // If this level matches the selection, show check icon
      if (withinHeadingSelection === level) {
        return CheckIcon
      }

      // Otherwise, show placeholder to maintain alignment
      return CheckIconPlaceholder
    },
    [withinHeadingSelection],
  )

  const runCommand = useCallback(
    (level: number, keybinding: KeyBinding) => {
      if (!view) return
      const isPressed = withinHeadingSelection === level
      const commandFn = keybinding.run || (() => false)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- Some commands accept optional event source metadata.
      const runWithEventSource = commandFn as (editorView: EditorView, eventSource?: 'keyboard' | 'toolbar') => boolean
      const handled = runWithEventSource(view, 'toolbar')

      telemetry.trackToolbarInteraction({
        control: `heading_${level}`,
        action: `heading_${level}`,
        trigger: 'toolbar',
        isActive: isPressed,
        keybinding: keybinding.key,
        handled,
      })
    },
    [telemetry, view, withinHeadingSelection],
  )

  const handleLevelAdjustment = useCallback(
    (direction: Direction) => {
      if (!view) return

      let handled = false
      if (direction === 'increase') handled = adjustHeadingLevel(view, 'increase')
      if (direction === 'decrease') handled = adjustHeadingLevel(view, 'decrease')

      telemetry.trackToolbarInteraction({
        control: `${direction}_heading_level`,
        action: `${direction}_heading_level`,
        trigger: 'toolbar',
        isActive: true,
        handled,
      })
    },
    [telemetry, view],
  )

  return (
    <ActionBar.Menu
      aria-label="Heading"
      icon={HeadingIcon}
      items={[
        {
          label: 'Heading 1',
          onClick: () => runCommand(1, formatHeading1Keybinding),
          leadingVisual: getLeadingVisual(1),
          trailingVisual: getTrailingVisual(formatHeading1Keybinding),
        },
        {
          label: 'Heading 2',
          onClick: () => runCommand(2, formatHeading2Keybinding),
          leadingVisual: getLeadingVisual(2),
          trailingVisual: getTrailingVisual(formatHeading2Keybinding),
        },
        {
          label: 'Heading 3',
          onClick: () => runCommand(3, formatHeading3Keybinding),
          leadingVisual: getLeadingVisual(3),
          trailingVisual: getTrailingVisual(formatHeading3Keybinding),
        },
        {
          label: 'Heading 4',
          onClick: () => runCommand(4, formatHeading4Keybinding),
          leadingVisual: getLeadingVisual(4),
          trailingVisual: getTrailingVisual(formatHeading4Keybinding),
        },
        {
          label: 'Heading 5',
          onClick: () => runCommand(5, formatHeading5Keybinding),
          leadingVisual: getLeadingVisual(5),
          trailingVisual: getTrailingVisual(formatHeading5Keybinding),
        },
        {
          label: 'Heading 6',
          onClick: () => runCommand(6, formatHeading6Keybinding),
          leadingVisual: getLeadingVisual(6),
          trailingVisual: getTrailingVisual(formatHeading6Keybinding),
        },
        {type: 'divider'},
        {
          label: 'Increase heading level',
          onClick: () => handleLevelAdjustment('increase'),
          disabled: !isHeadingLevelChangeEnabled('increase'),
        },
        {
          label: 'Decrease heading level',
          onClick: () => handleLevelAdjustment('decrease'),
          disabled: !isHeadingLevelChangeEnabled('decrease'),
        },
      ]}
    />
  )
}
